import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { CheckIcon, ArrowUpRightIcon } from '@heroicons/react/24/solid'
import { DocumentDuplicateIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
import { showSimpleSuccess } from '@/lib/toast'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { publish } from '@/assets/images'

// Step durations based on project type
const AI_COMPANION_STEP_DURATION = 20000  // 20 seconds per step for AI companion
const REGULAR_STEP_DURATION = 5000        // 5 seconds per step for regular projects
const FAST_COMPLETION_DURATION = 300      // 300ms per step when success comes

const PROGRESS_INTERVAL = 16
const PROGRESS_PER_STEP = 20
const BUTTON_BASE_CLASS = "!bg-white !shadow-[0px_1px_2px_0px_#0000000D] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"

function CircularProgress({ isActive, stepKey, shouldRepeat, stepDuration }) {
  const [percentage, setPercentage] = useState(0)
  const timerRef = useRef(null)
  const progressIncrement = (100 / stepDuration) * PROGRESS_INTERVAL

  useEffect(() => {
    if (!isActive) {
      setPercentage(0)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (timerRef.current) clearInterval(timerRef.current)

    let current = 1
    setPercentage(1)

    timerRef.current = setInterval(() => {
      current += progressIncrement
      if (current >= 100) {
        if (shouldRepeat) {
          current = 1
          setPercentage(1)
        } else {
          setPercentage(100)
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      } else {
        setPercentage(current)
      }
    }, PROGRESS_INTERVAL)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isActive, stepKey, shouldRepeat, progressIncrement])

  if (!isActive) {
    return <div className="w-4 h-4 rounded-full border-2 border-[#A1A1AA] flex-shrink-0" />
  }

  return (
    <div style={{ width: '16px', height: '16px', minWidth: '16px' }} className="flex-shrink-0">
      <CircularProgressbar
        styles={buildStyles({
          pathTransition: percentage === 0 ? 'none' : 'stroke-dashoffset 0.1s linear 0s',
          pathColor: '#71717A',
          trailColor: '#A1A1AA',
        })}
        strokeWidth={10}
        value={percentage}
      />
    </div>
  )
}

const STEPS = [
  'Optimizing assets',
  'Building page',
  'Deploying to servers',
  'Configuring DNS',
  'Finalizing publish',
]

export const PublishStatus = memo(function PublishStatus({
  isPublishing,
  displayUrl,
  onViewLiveSite,
  onDone,
  onNavigateToPage,
  onClose,
  publishInfo,
  publishState,
  isAiCompanionMode = false
}) {
  const [activeStepIndex, setActiveStepIndex] = useState(-1)
  const [completedSteps, setCompletedSteps] = useState(new Set())
  const [overallProgress, setOverallProgress] = useState(0)
  const [allStepsShown, setAllStepsShown] = useState(false)
  const refs = useRef({
    hasStarted: false,
    isActive: true,
    timeouts: [],
    progressInterval: null,
    stepStartTime: null,
    activeStepIndex: -1,
    successReceived: false
  })

  // Determine step duration based on project type and success state
  const getStepDuration = useCallback(() => {
    if (refs.current.successReceived) return FAST_COMPLETION_DURATION
    return isAiCompanionMode ? AI_COMPANION_STEP_DURATION : REGULAR_STEP_DURATION
  }, [isAiCompanionMode])

  const isSuccess = publishState === 'success' || (!isPublishing && (allStepsShown || overallProgress >= 100))
  const progressWidth = isSuccess ? 100 : overallProgress

  const reset = useCallback(() => {
    Object.assign(refs.current, { hasStarted: false, isActive: false, successReceived: false })
    setActiveStepIndex(-1)
    setCompletedSteps(new Set())
    setOverallProgress(0)
    setAllStepsShown(false)
  }, [])

  const clearTimers = useCallback(() => {
    refs.current.timeouts.forEach(clearTimeout)
    refs.current.timeouts = []
    if (refs.current.progressInterval) {
      clearInterval(refs.current.progressInterval)
      refs.current.progressInterval = null
    }
  }, [])

  const updateStep = useCallback((index) => {
    refs.current.activeStepIndex = index
    refs.current.stepStartTime = Date.now()
    setActiveStepIndex(index)
  }, [])

  const completeStep = useCallback((index, isLastStep) => {
    setCompletedSteps(prev => new Set([...prev, index]))
    if (isLastStep) {
      setAllStepsShown(true)
      setOverallProgress(100)
    } else {
      setOverallProgress((index + 1) * PROGRESS_PER_STEP)
    }
  }, [])

  // Fast-forward remaining steps when success is received
  const fastForwardToSuccess = useCallback(() => {
    if (refs.current.successReceived) return
    refs.current.successReceived = true

    // Clear existing timers
    clearTimers()

    // Complete remaining steps quickly
    const currentStep = refs.current.activeStepIndex
    let delay = 0

    for (let i = currentStep; i < STEPS.length; i++) {
      const stepDelay = delay
      const isLastStep = i === STEPS.length - 1

      refs.current.timeouts.push(setTimeout(() => {
        if (i > currentStep) updateStep(i)
        setTimeout(() => {
          completeStep(i, isLastStep)
        }, FAST_COMPLETION_DURATION / 2)
      }, stepDelay))

      delay += FAST_COMPLETION_DURATION
    }
  }, [clearTimers, updateStep, completeStep])

  const handleCopyUrl = useCallback(() => {
    if (displayUrl) {
      navigator.clipboard.writeText(displayUrl)
      showSimpleSuccess('URL copied to clipboard')
    }
  }, [displayUrl])

  const handleOpenSettings = useCallback(() => {
    onNavigateToPage(publishInfo?.isDomainActive ? '/domain'
      : publishInfo?.isWordpressActive ? '/wordpress'
        : '/settings')
    onClose?.()
  }, [publishInfo?.isDomainActive, publishInfo?.isWordpressActive, onNavigateToPage, onClose])

  // Handle success response - speed up completion
  useEffect(() => {
    if (publishState === 'success' && refs.current.hasStarted && !refs.current.successReceived) {
      fastForwardToSuccess()
    }
  }, [publishState, fastForwardToSuccess])

  // Main publishing effect
  useEffect(() => {
    if (!isPublishing && publishState !== 'success') {
      if (allStepsShown || overallProgress >= 100) {
        setOverallProgress(100)
        setCompletedSteps(new Set(STEPS.map((_, i) => i)))
        setActiveStepIndex(-1)
        setAllStepsShown(true)
      } else if (!refs.current.hasStarted) {
        reset()
        clearTimers()
      }
      return
    }

    if (refs.current.hasStarted) return

    const stepDuration = getStepDuration()
    const now = Date.now()
    Object.assign(refs.current, { hasStarted: true, isActive: true, stepStartTime: now, activeStepIndex: 0 })
    clearTimers()
    setActiveStepIndex(0)

    STEPS.forEach((_, index) => {
      const startDelay = index * stepDuration
      const isLastStep = index === STEPS.length - 1

      if (index > 0) {
        refs.current.timeouts.push(setTimeout(() => {
          if (refs.current.isActive && !refs.current.successReceived) updateStep(index)
        }, startDelay))
      }

      refs.current.timeouts.push(setTimeout(() => {
        if (refs.current.isActive && !refs.current.successReceived) completeStep(index, isLastStep)
      }, startDelay + stepDuration))
    })

    refs.current.progressInterval = setInterval(() => {
      if (!refs.current.isActive || refs.current.activeStepIndex < 0) return

      const { activeStepIndex: stepIdx, stepStartTime } = refs.current
      const currentStepDuration = getStepDuration()
      const elapsed = Date.now() - stepStartTime
      const stepProgress = Math.min((elapsed / currentStepDuration) * PROGRESS_PER_STEP, PROGRESS_PER_STEP)
      const totalProgress = stepIdx * PROGRESS_PER_STEP + stepProgress
      const maxProgress = stepIdx === STEPS.length - 1 ? 100 : (stepIdx + 1) * PROGRESS_PER_STEP
      setOverallProgress(Math.min(totalProgress, maxProgress))
    }, PROGRESS_INTERVAL)

    return () => {
      if (!isPublishing && !allStepsShown && publishState !== 'success') {
        clearTimers()
        refs.current.isActive = false
      }
      if (isPublishing) refs.current.hasStarted = false
    }
  }, [isPublishing, publishState, reset, clearTimers, allStepsShown, updateStep, completeStep, getStepDuration])



  return (
    <div className="py-3">
      <div className="flex items-center justify-center border-b border-[#E4E4E7] pb-4 px-4 gap-2">
        {isSuccess && <CheckIcon className="w-4 h-4 text-white bg-[#22C55E] rounded-full flex-shrink-0 p-0.5 border-[1.5px] border-[#15803D]" />}
        <h3 className={`text-sm text-center font-semibold ${isSuccess ? 'text-[#15803D]' : 'text-[#09090B]'}`}>
          {isSuccess ? 'Successfully Published!' : 'Publishing your site...'}
        </h3>
      </div>

      <div className="space-y-2 py-3 px-4 border-b border-[#E4E4E7]">
        {isSuccess ? (
          <p className="text-xs text-[#71717A] font-medium text-center">
            Your website is now live and accessible to the world
          </p>
        ) : (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#09090B] font-medium">Overall progress</span>
            <span className="text-[#71717A] font-medium">{Math.round(overallProgress)}%</span>
          </div>
        )}
        <div className="w-full h-1.5 bg-[#E4E4E7] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#22C55E] transition-all duration-300 ease-out"
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      </div>

      {isSuccess ? (
        <div className="bg-[#F4F4F5]">
          <div className="flex items-center gap-2 px-4 py-2">
            <Button outline onClick={handleCopyUrl} className={`p-0 ${BUTTON_BASE_CLASS}`}>
              <DocumentDuplicateIcon className="w-4 h-4 text-[#71717A] flex-shrink-0" />
            </Button>
            <span className="flex-1 text-sm font-medium text-[#71717A] truncate">{displayUrl}</span>
            <Button
              outline
              onClick={handleOpenSettings}
              className={`ml-auto ${BUTTON_BASE_CLASS}`}
              title={publishInfo?.isDomainActive ? 'Domain Settings' : publishInfo?.isWordpressActive ? 'WordPress Settings' : 'Project Settings'}
            >
              <Cog6ToothIcon className="w-4 h-4 !text-[#09090B]" />
            </Button>
          </div>
          {/* 
        //COMMMENT OUT LATER!!!
          <div className="bg-[#FAFAFA] border-t border-[#E4E4E7] px-4 py-3">
            <div className="bg-gradient-to-b from-[#5C33FF00] to-[#5C33FF0D] border border-[#5C33FF33] rounded-lg p-2.5 flex items-center justify-between shadow-[0px_1px_2px_0px_#0000000D]">
              <div className="flex items-center gap-3">
                <img src={publish} alt="Publish" className="w-9 h-9 rounded-[1px] text-[#71717A]" />
                <div>
                  <p className="text-sm font-semibold text-[#71717A]">
                    Proud of this build? 
                  </p>
                  <p className='text-xs text-[#A1A1AA] font-medium'>
                    Showcase it to the CodeDesign Community.
                  </p>
                </div>
              </div>
              <ArrowUpRightIcon className="w-5 h-5 !text-[#09090B] flex-shrink-0" />
            </div>
          </div> */}

        </div>
      ) : (
        <div className="space-y-3 py-3 px-4 bg-[#F4F4F5]">
          {STEPS.map((label, index) => {
            const isCompleted = completedSteps.has(index)
            const isInProgress = activeStepIndex === index
            const textColor = isCompleted || isInProgress ? 'text-[#71717A]' : 'text-[#A1A1AA]'

            return (
              <div key={index} className="flex items-center gap-3">
                {isCompleted ? (
                  <CheckIcon className="w-4 h-4 text-white bg-[#22C55E] rounded-full flex-shrink-0 p-0.5 border-[1.5px] border-[#15803D]" />
                ) : isInProgress ? (
                  <CircularProgress
                    isActive
                    stepKey={`${index}-${activeStepIndex}`}
                    shouldRepeat={false}
                    stepDuration={getStepDuration()}
                  />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-[#A1A1AA] flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${textColor}`}>{label}</span>
                {isInProgress && <span className="text-xs text-[#71717A] ml-auto">In progress...</span>}
                {isCompleted && <span className="text-xs text-[#A1A1AA] ml-auto">Completed</span>}
              </div>
            )
          })}
        </div>
      )}

      {isSuccess ? (
        <div className="flex gap-3 px-4 border-t border-[#E4E4E7] pt-3.5">
          <Button
            outline
            onClick={onDone}
            className="flex-1 items-center justify-center !bg-white whitespace-nowrap !border-[#E4E4E7] !text-[#09090B] !rounded-lg !text-sm !font-semibold !py-2 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            Done
          </Button>
          <Button
            color="primary"
            onClick={onViewLiveSite}
            className="flex-1 items-center justify-center !bg-[#5C33FF] whitespace-nowrap text-white hover:!bg-[#5C33FF]/90 !rounded-lg !text-sm !font-semibold !py-2 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            View Live Site
            <ArrowUpRightIcon className="!w-4 !h-4 !text-white !ml-1" />
          </Button>
        </div>
      ) : (
        <p className="text-sm text-[#71717A] font-medium text-center px-4 py-3 border-t border-[#E4E4E7]">
          The publishing process typically takes 1-2 minutes to complete
        </p>
      )}
    </div>
  )
})


